import { VisibilityOffRounded, VisibilityRounded } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { UpdatePassword } from '../../api/external-users';
import { GetForgotPasswordParams } from '../../api/reset-password-external-users';
import { RootState } from '../../app/store';
import { useAppDispatch } from '../../hook/useAppRudux';
import { updatePasswordExternalUserAsync } from '../ExternalUser/externalUserSlice';
import loadingSlice from '../Loading/loadingSlice';
import { getForgotPasswordAsync } from '../ResetPassword/resetPasswordSlice';

interface AccountState {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  showOldPassword: boolean;
  showNewPassword: boolean;
  showConfirmNewPassword: boolean;
}

const styles = getStyles();

export default function ChangePassword() {
  const dispatch = useAppDispatch();

  const { user } = useSelector((state: RootState) => state.userProfile);

  const [account, setAccount] = useState<AccountState>({
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    showOldPassword: false,
    showNewPassword: false,
    showConfirmNewPassword: false
  });
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);

  const disabled =
    (account.oldPassword.length < 8 ||
      account.newPassword.length < 8 ||
      account.confirmNewPassword.length < 8 ||
      account.newPassword !== account.confirmNewPassword) &&
    !forgotPassword;

  function handleChangePassword(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void {
    const { name, value } = event.target;
    setAccount({ ...account, [name]: value });
  }

  function handleClickShowPassword(key: keyof AccountState): void {
    setAccount({ ...account, [key]: !account[key] });
  }

  function onClickForgotPassword() {
    setForgotPassword((prev) => !prev);
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      if (forgotPassword) {
        const email = user?.email;
        if (email) {
          const forgotPassword: GetForgotPasswordParams = {
            email
          };
          await dispatch(getForgotPasswordAsync(forgotPassword)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: 'Password reset email was sent, please check your inbox'
            })
          );
        } else {
          throw new Error('Email is invalid');
        }
      } else {
        const updatePassword: UpdatePassword = {
          oldPassword: account.oldPassword,
          newPassword: account.newPassword
        };
        await dispatch(
          updatePasswordExternalUserAsync(updatePassword)
        ).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: 'Your password has been changed successfully'
          })
        );
      }
    } catch (error) {
      if (error instanceof Error)
        dispatch(
          loadingSlice.actions.error({
            message: error.message
          })
        );
    } finally {
      setAccount({
        ...account,
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
    }
  }

  return (
    <Box sx={styles.pageContainer} maxWidth="sm">
      <Typography component="p" variant="h4" sx={styles.title}>
        {forgotPassword ? 'Reset Password' : 'Change Password'}
      </Typography>
      <Paper sx={styles.paper}>
        <form onSubmit={onSubmit}>
          <Alert sx={styles.alert} severity="info">
            {forgotPassword
              ? `We will send you a password reset link to "${user?.email}"`
              : 'Your password must be at least 8 characters and contain at least one letter and one number'}
          </Alert>
          {!forgotPassword && (
            <>
              <PasswordField
                label="Current Password"
                name="oldPassword"
                value={account.oldPassword}
                showPassword={account.showOldPassword}
                onChange={handleChangePassword}
                onToggleVisibility={() =>
                  handleClickShowPassword('showOldPassword')
                }
                placeholder="Enter current password..."
              />
              <PasswordField
                label="New Password"
                name="newPassword"
                value={account.newPassword}
                showPassword={account.showNewPassword}
                onChange={handleChangePassword}
                onToggleVisibility={() =>
                  handleClickShowPassword('showNewPassword')
                }
                placeholder="Enter new password..."
              />
              <PasswordField
                label="Confirm New Password"
                name="confirmNewPassword"
                value={account.confirmNewPassword}
                showPassword={account.showConfirmNewPassword}
                onChange={handleChangePassword}
                onToggleVisibility={() =>
                  handleClickShowPassword('showConfirmNewPassword')
                }
                placeholder="Enter confirm new password..."
                error={
                  account.confirmNewPassword.length > 0 &&
                  account.newPassword !== account.confirmNewPassword
                }
                errorText="New password and confirmation do not match"
              />
            </>
          )}
          <Button
            sx={styles.forgotButton}
            color="inherit"
            disableRipple
            onClick={onClickForgotPassword}
          >
            {forgotPassword ? 'Remember? Change password' : 'Forgot password?'}
          </Button>
          <Button
            sx={styles.submitButton}
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={disabled}
            fullWidth
          >
            {forgotPassword ? 'Send reset link' : 'Change password'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}

interface PasswordFieldProps {
  label: string;
  name: keyof AccountState;
  value: string;
  showPassword: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleVisibility: () => void;
  placeholder: string;
  error?: boolean;
  errorText?: string;
}

function PasswordField({
  label,
  name,
  value,
  showPassword,
  onChange,
  onToggleVisibility,
  placeholder,
  error = false,
  errorText = ''
}: PasswordFieldProps) {
  return (
    <FormControl sx={styles.password} variant="outlined" size="small">
      <InputLabel htmlFor={name} error={error}>
        {label}
      </InputLabel>
      <OutlinedInput
        id={name}
        type={showPassword ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={onToggleVisibility}
              onMouseDown={(e) => e.preventDefault()}
              edge="end"
              size="large"
            >
              {showPassword ? (
                <Tooltip title="Hide password">
                  <VisibilityOffRounded />
                </Tooltip>
              ) : (
                <Tooltip title="Show password">
                  <VisibilityRounded />
                </Tooltip>
              )}
            </IconButton>
          </InputAdornment>
        }
        label={label}
        placeholder={placeholder}
        error={error}
      />
      <FormHelperText error={error}>{error ? errorText : ''}</FormHelperText>
    </FormControl>
  );
}

function getStyles() {
  const pageContainer = {
    mb: 10,
    mx: 'auto'
  };

  const title = {
    mt: { xs: 3.5, sm: 1.5 },
    mb: { xs: 4, sm: 2 },
    textAlign: { xs: 'center', sm: 'left' }
  };

  const paper = { p: 2, mb: 2, borderRadius: '16px' };

  const alert = { mb: 2 };

  const password = { mb: 1.5, width: '100%' };
  const confirmPassword = { width: '100%' };

  const forgotButton = {
    mt: -2,
    textTransform: 'none',
    textDecoration: 'underline',
    padding: '6px 8px',
    marginBottom: 1,
    '&:hover, &.Mui-focusVisible': {
      color: 'primary.main',
      backgroundColor: 'transparent'
    }
  };

  const submitButton = { my: 1, fontSize: 16 };

  return {
    pageContainer,
    title,
    paper,
    alert,
    password,
    confirmPassword,
    forgotButton,
    submitButton
  };
}
